// El REGISTRO que una card de generación (imagen o video) deja en el historial para los
// turnos siguientes — ADR-2026-09-26-el-mismo-prompt-se-copia-del-registro.
//
// Los `tool_use` de una ronda no se vuelven a mandar: lo único que el agente conserva de
// lo que generó es esta línea. Hasta acá tenía dos agujeros que se vieron en un chat de
// prueba (2026-09-26): una card DESCARTADA no dejaba nada, así que "hacé el mismo prompt
// pero con este escritorio" no tenía de dónde copiar; y el prompt de una card resuelta
// viajaba recortado a 400 caracteres, así que "el mismo prompt de 30 s con dos cambios"
// se reescribía de memoria y salía otro. Ahora las dos dejan registro y el prompt va
// entero (con un tope alto, por si alguien pega un libro).
//
// Puro: (card) → una línea o null.

/** Hasta dónde viaja el prompt en el registro. Un prompt de video de 30 s con nueve beats
 *  tiene ~2.700 caracteres y los usuarios pegan de hasta ~3.000; el tope es sólo para no
 *  arrastrar un pegado descomunal. */
export const TOPE_PROMPT_EN_REGISTRO = 6000;

/** Para los campos cortos (un cuadro, una escena): en una línea. */
const clip = (v: unknown, n: number): string => {
  const s = String(v ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

/** El prompt va TAL CUAL (saltos de línea incluidos): el pedido es copiarlo literal, y los
 *  beats de un video viven en líneas separadas (revisión de Greptile al PR #148). */
const promptLiteral = (v: unknown): string => {
  const s = String(v ?? '').trim();
  return s.length > TOPE_PROMPT_EN_REGISTRO ? `${s.slice(0, TOPE_PROMPT_EN_REGISTRO)}…` : s;
};

export interface CardDeGeneracion {
  kind: string;
  status: 'pending' | 'resolved' | 'cancelled';
  input?: Record<string, any> | null;
  response?: Record<string, any> | null;
}

const ES_IMAGEN = new Set(['generate_image', 'confirm_generate']);

/** Qué copiar cuando el usuario pide "el mismo prompt": la instrucción va en el registro
 *  mismo, que es donde el agente la va a leer. */
export const COMO_REUSAR =
  'Si pide "el mismo prompt" (con o sin un cambio), copiá ESTE prompt literal y cambiá sólo lo que pide.';

function configDeVideo(inp: Record<string, any>): string[] {
  const cfg: string[] = [];
  if (inp.prompt) cfg.push(`prompt="${promptLiteral(inp.prompt)}"`);
  if (inp.format) cfg.push(`formato=${inp.format}`);
  if (inp.duration) cfg.push(`duración=${inp.duration}s`);
  if (inp.quality) cfg.push(`calidad=${inp.quality}`);
  if (inp.resolution) cfg.push(`resolución=${inp.resolution}`);
  for (const [k, f] of [
    ['first_frame', inp.first_frame],
    ['last_frame', inp.last_frame],
  ] as const) {
    if (f?.source)
      cfg.push(
        `${k}={source:"${f.source}"${f.prompt ? `, prompt:"${clip(f.prompt, 300)}"` : ''}${f.image_id ? `, image_id:"${f.image_id}"` : ''}${f.adjunto_id ? `, adjunto_id:"${f.adjunto_id}"` : ''}}`,
      );
  }
  if (Array.isArray(inp.reference_images) && inp.reference_images.length > 0)
    cfg.push(
      `referencias=[${inp.reference_images
        .map((r: any) => {
          const id = r.product_id || r.model_id || r.image_id || r.adjunto_id;
          return `${r.source}${id ? `:${id}` : ''}`;
        })
        .join(', ')}]`,
    );
  // Con `archivo`: con varios videos adjuntos es lo que dice CUÁL se editó; sin él, una
  // llamada nueva agarra el más reciente (revisión de Greptile al PR #148).
  if (inp.reference_video?.modo || inp.reference_video?.archivo)
    cfg.push(
      `reference_video={modo:"${inp.reference_video.modo || 'editar'}"${inp.reference_video.archivo ? `, archivo:"${clip(inp.reference_video.archivo, 120)}"` : ''}${typeof inp.reference_video.desde === 'number' ? `, desde:${inp.reference_video.desde}` : ''}}`,
    );
  return cfg;
}

function configDeImagen(inp: Record<string, any>): string[] {
  const cfg: string[] = [];
  if (inp.prompt) cfg.push(`prompt="${promptLiteral(inp.prompt)}"`);
  if (inp.scene_summary) cfg.push(`escena="${clip(inp.scene_summary, 200)}"`);
  if (Array.isArray(inp.product_ids) && inp.product_ids.length > 0)
    cfg.push(`productos=[${inp.product_ids.join(', ')}]`);
  if (inp.model_id) cfg.push(`modelo=${inp.model_id}`);
  if (Array.isArray(inp.pose_ids) && inp.pose_ids.length > 0) cfg.push(`poses=[${inp.pose_ids.join(', ')}]`);
  if (inp.background?.mode)
    cfg.push(
      `fondo=${inp.background.mode}${inp.background.text ? ` "${clip(inp.background.text, 150)}"` : ''}${inp.background.id ? ` id=${inp.background.id}` : ''}`,
    );
  if (inp.instruccion) cfg.push(`instrucción="${clip(inp.instruccion, 200)}"`);
  if (inp.format) cfg.push(`formato=${inp.format}`);
  if (inp.cantidad && Number(inp.cantidad) > 1) cfg.push(`cantidad=${inp.cantidad}`);
  if (Array.isArray(inp.reference_images) && inp.reference_images.length > 0)
    cfg.push(
      `referencias=[${inp.reference_images
        .map((r: any) => {
          const id = r.product_id || r.model_id || r.image_id || r.pose_id || r.fondo_id || r.adjunto_id;
          return `${r.source}${id ? `:${id}` : ''}`;
        })
        .join(', ')}]`,
    );
  return cfg;
}

/**
 * Cómo se rotula una card cancelada. "Antes de generar" es lo normal (el usuario la
 * descartó o cambió de tema). Pero si el turno se cortó con la generación YA lanzada
 * (`pending_generation` en el input y el resultado no se recuperó), decir "antes de
 * generar" sería mentirle al agente: pudo haber salido igual (revisión de Greptile al
 * PR #148).
 */
const rotuloDeCancelada = (que: 'video' | 'imagen', inp: Record<string, any>): string =>
  inp.pending_generation
    ? `${que} ${que === 'video' ? 'cancelado' : 'cancelada'} con la generación YA lanzada (pudo haber salido igual: está en Creaciones si terminó)`
    : `${que} ${que === 'video' ? 'DESCARTADO' : 'DESCARTADA'} por el usuario antes de generar`;

/**
 * La línea de registro de una card de video o imagen, o null si la card no es de ésas o
 * está pendiente. Las resueltas llevan lo generado (ids) y su config; las canceladas,
 * la config sola, rotuladas según si la generación llegó a lanzarse.
 */
export function registroDeGeneracionParaHistorial(c: CardDeGeneracion): string | null {
  const inp = c.input || {};
  if (c.kind === 'generate_video') {
    if (c.status === 'cancelled') {
      const cfg = configDeVideo(inp);
      return cfg.length ? `[registro: ${rotuloDeCancelada('video', inp)} — ${cfg.join(' · ')}. ${COMO_REUSAR}]` : null;
    }
    if (c.status !== 'resolved' || !c.response?.video_url) return null;
    const cfg = configDeVideo(inp);
    const frameIds = c.response?.first_frame_image_id
      ? ` Primer frame = image_id=${c.response.first_frame_image_id}${
          c.response.last_frame_image_id ? `, último frame = image_id=${c.response.last_frame_image_id}` : ''
        }. Para re-editar ese video manteniendo la escena, pasá first_frame {source:"generada", image_id:"${c.response.first_frame_image_id}"} — no regeneres la imagen.`
      : '';
    return `[registro: video generado OK — ${cfg.join(' · ')}.${frameIds} ${COMO_REUSAR}]`;
  }
  if (ES_IMAGEN.has(c.kind)) {
    if (c.status === 'cancelled') {
      const cfg = configDeImagen(inp);
      return cfg.length ? `[registro: ${rotuloDeCancelada('imagen', inp)} — ${cfg.join(' · ')}. ${COMO_REUSAR}]` : null;
    }
    if (c.status !== 'resolved' || !Array.isArray(c.response?.image_ids) || c.response.image_ids.length === 0) return null;
    const cfg = configDeImagen(inp);
    return `[registro: imagen(es) generada(s) OK — image_ids=${c.response.image_ids.join(', ')} (usables en video como source "generada").${cfg.length ? ` Config: ${cfg.join(' · ')}.` : ''} ${COMO_REUSAR}]`;
  }
  return null;
}
